import { sessionFetch } from "@/lib/session";
/**
 * Auth helpers for the cookie-based backend contract.
 *
 * Auth is carried by httpOnly cookies set by the backend, so every request uses
 * `credentials: "include"` and the browser attaches cookies automatically.
 *
 * The login / OTP / MFA flows live in `@/app/login` (ported from the Next app);
 * this module holds the shared session helpers used by the router and shell.
 * All requests hit the API directly at the runtime `apiUrl` origin from
 * `/config.json` (cross-origin + CORS).
 */
import { queryOptions } from "@tanstack/react-query";
import apiClient, { refreshOnce } from "@/lib/api-client";
import { AuthError } from "@/lib/errors";
import { apiUrl } from "@/lib/apiBase";
import type { PlatformRole } from "@/hooks/api/useAccessManagement";

export interface MeResponse {
  id?: string;
  email?: string;
  name?: string;
  is_admin?: boolean;
  is_super_admin?: boolean;
  mfa_required?: boolean;
  mfa_verified?: boolean;
  /** The full `users` DB row (id, email, first_name, last_name, ...) — nested here by `/v1/auth/me`, not at the top level. */
  profile?: Record<string, any> | null;
  /** Database the API is connected to (from its SUPABASE_URL); used to flag staging. */
  database_environment?: "production" | "staging" | "unknown";
  /** The user's platform role; `null` when they have none or the API couldn't look it up. */
  role?: PlatformRole | null;
  [key: string]: unknown;
}

/**
 * Fetch the current user. Throws `AuthError` on 401 (the layout `beforeLoad`
 * relies on a thrown error to redirect to `/login`).
 */
export async function fetchMe(): Promise<MeResponse> {
  const { data, error, response } = await apiClient.GET("/v1/auth/me");
  if (error || !response?.ok) {
    const status = response?.status ?? 0;
    // A 401 redirects to login without retrying. Other statuses, including
    // forbidden (403), do not prove the session ended; leave them to the
    // query retry policy instead of forcing logout.
    if (status === 401) {
      throw new AuthError("Not authenticated", status);
    }
    throw new Error(`Failed to load session (status ${status})`);
  }
  return (data ?? {}) as MeResponse;
}

/**
 * TanStack Query options for the current user. Built with `queryOptions` so
 * route `beforeLoad` / `ensureQueryData` infer `MeResponse`.
 *
 * Transient failures (network / 5xx) are retried up to 3× with backoff so a
 * momentary hiccup doesn't force an authenticated user to re-login (mirrors the
 * old Next `AuthWrapper` behaviour); a 401 (`AuthError`) never retries.
 */
export const meQueryOptions = queryOptions({
  queryKey: ["auth", "me"],
  queryFn: fetchMe,
  retry: (failureCount, error) => {
    if (error instanceof AuthError) {
      return false;
    }
    return failureCount < 3;
  },
  retryDelay: (attempt) => [500, 1000, 2000][attempt] ?? 2000,
  staleTime: 5 * 60 * 1000,
});

/**
 * Verify or recover the session through the shared recovery coordinator.
 * The api-client and keep-alive hook share the same recovery job and tab lock.
 * Returns whether the session is authenticated afterward.
 */
export async function refreshSession(): Promise<boolean> {
  return (await refreshOnce()).ok;
}

/** Revoke the refresh token and clear cookies. */
export async function logout(): Promise<void> {
  try {
    await sessionFetch(apiUrl("/v1/auth/mfa/logout"), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    // Swallow: the UI proceeds to the login screen regardless.
  }
}
